import type { ReactElement, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { captureFrontendException } from './sentry';

interface FallbackRenderProps {
  /** The error captured by the boundary. */
  readonly error: unknown;
  /** Clears the boundary's error state — re-renders `children` on the
   * next React tree pass. Use for "Try again" affordances. */
  readonly resetError: () => void;
}

type Fallback = ReactNode | ((props: FallbackRenderProps) => ReactNode);

interface Props {
  children: ReactNode;
  /**
   * Optional fallback. Two shapes accepted:
   *   - `ReactNode` — static element rendered on error.
   *   - `({ error, resetError }) => ReactNode` — render prop, lets the
   *     consumer wire a "Try again" button via `resetError`.
   *
   * Omit for the default fallback (greppable `role="alert"` block).
   */
  fallback?: Fallback;
}

/**
 * Top-level render-tree error boundary.
 *
 * Delegates to `@sentry/react`'s `Sentry.ErrorBoundary` so we inherit its
 * first-class behavior: `resetError`-driven auto-clear, session-replay
 * integration when enabled, and the `beforeCapture` hook for setting
 * scope tags on the Sentry event.
 *
 * **Bundle gating tradeoff (2026-05-19).** The previous bespoke
 * implementation existed to keep `@sentry/react` out of the initial
 * chunk when no DSN is configured (via the lazy `await import` inside
 * `./sentry.ts`). Measured cost of the swap-to-static-import: +5.79 KB
 * gzipped on the initial vendor chunk (baseline 638.29 KB → 644.19 KB).
 * Under the 10 KB threshold the audit set; the gating preservation
 * isn't worth the wrapper complexity.
 *
 * **Capture path.** `Sentry.ErrorBoundary` captures via its own
 * `Sentry.captureException` call before invoking our `onError`. We
 * attach our custom tags through `beforeCapture` so the Sentry-side
 * event carries them. The explicit `captureFrontendException(err, …)`
 * call in `onError` is retained for two reasons: (1) it's the seam
 * the W91 / FE-pipeline tests assert against (mock target is
 * `./sentry`), and (2) the resulting duplicate-Sentry-events are
 * folded by Sentry's default `dedupeIntegration` server-side. When
 * the SDK isn't initialized (no DSN), both paths are no-ops.
 */
export function SentryErrorBoundary({
  children,
  fallback,
}: Props): JSX.Element {
  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag('source', 'error_boundary');
        scope.setTag(
          'route',
          typeof window !== 'undefined' ? window.location.pathname : '<ssr>',
        );
      }}
      fallback={(errorData): ReactElement => {
        const { error, resetError } = errorData;
        if (typeof fallback === 'function') {
          return <>{fallback({ error, resetError })}</>;
        }
        if (fallback !== undefined) {
          return <>{fallback}</>;
        }
        return (
          <div role="alert" className="p-8 font-sans">
            <h1>Something went wrong</h1>
            <p>The page hit an unexpected error. Try reloading.</p>
            {error instanceof Error && error.message ? (
              <pre className="whitespace-pre-wrap opacity-70">
                {error.message}
              </pre>
            ) : null}
            <button type="button" onClick={resetError} className="mt-4">
              Try again
            </button>
          </div>
        );
      }}
      onError={(error) => {
        // Mirror the capture through our seam so dev/test runs without
        // a DSN still see the call path, and so the FE-pipeline test
        // asserting `captureFrontendException` is called continues to
        // pass. Sentry's `dedupeIntegration` (enabled by default in
        // @sentry/react v8) folds the resulting duplicate when the SDK
        // IS initialized.
        captureFrontendException(error, {
          source: 'error_boundary',
          route:
            typeof window !== 'undefined' ? window.location.pathname : '<ssr>',
        });
        console.error('[error-boundary]', error);
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
